import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updateProfile,
  setPersistence,
  indexedDBLocalPersistence,
  User as FirebaseUser
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { analytics } from '@lib/analytics';
import { getPersistedUtm, clearUtmData } from '@/lib/referral';
import { useCurrencyStore } from '@/stores/currency-store';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const firebaseAuth = getAuth(app);
const db = getFirestore(app);

// Set persistence to IndexedDB explicitly (more reliable than browserLocalPersistence)
setPersistence(firebaseAuth, indexedDBLocalPersistence)
  .catch((error) => console.error('[Auth] Failed to set persistence:', error));

// API URL for backend
const API_URL = import.meta.env.VITE_API_URL;

// Flag to track force sign-out (prevents double callback when signOut triggers onAuthStateChanged)
let isForceSigningOut = false;

export interface User {
  id: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'user' | 'viewer';
  workspaceId?: string;
  workspaceName?: string;
}

// Custom error types for better error handling
class AuthError extends Error {
  constructor(message: string, public statusCode: number) {
    super(message);
    this.name = 'AuthError';
  }
}

interface SyncResult {
  user: User
  isNew: boolean
}

// Helper function to sync with backend
async function syncWithBackend(firebaseUser: FirebaseUser): Promise<SyncResult> {
  const idToken = await firebaseUser.getIdToken();

  // Read UTM data from localStorage/cookie (persisted before OAuth redirect)
  const utmData = getPersistedUtm();

  // Updated: use /api/auth/join with Bearer token
  const response = await fetch(`${API_URL}/api/auth/join`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...(utmData && { utmData }),
    }),
  });

  if (!response.ok) {
    const statusCode = response.status;
    let errorMessage = 'Failed to sync with backend';

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }

    // Throw error with status code for proper handling
    throw new AuthError(errorMessage, statusCode);
  }

  const data = await response.json();

  // Clear UTM data after it's been sent to the backend — it's consumed.
  // Prevents stale cookie from attributing the next signup in the same browser.
  if (utmData) {
    clearUtmData();
  }

  return { user: data.user, isNew: data.is_new ?? false };
}

export const auth = {
  currentUser: null as User | null,
  
  async signIn(email: string, password: string): Promise<{ user: User; token: string }> {
    try {
      // Sign in with Firebase
      const credential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const idToken = await credential.user.getIdToken();

      // Sync with backend
      const { user } = await syncWithBackend(credential.user);
      this.currentUser = user;
      analytics.identify(user.id, user.workspaceId, user.email, user.workspaceName);
      analytics.track('login', { method: 'email' });

      return { user, token: idToken };
    } catch (error: any) {
      // Handle Firebase auth errors
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        throw new Error('Invalid email or password');
      }

      // Handle backend sync errors
      if (error instanceof AuthError) {
        if (error.statusCode === 403) {
          throw new Error('Account is deactivated. Please contact support.');
        } else if (error.statusCode === 500) {
          throw new Error('Server error. Please try again later.');
        }
      }

      throw error;
    }
  },
  
  async signUp(email: string, password: string, displayName?: string): Promise<{ user: User; token: string }> {
    try {
      // Create user in Firebase
      const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password);

      // Update display name if provided
      if (displayName) {
        await updateProfile(credential.user, { displayName });
      }

      const idToken = await credential.user.getIdToken();

      // Sync with backend
      const { user, isNew } = await syncWithBackend(credential.user);
      this.currentUser = user;
      analytics.identify(user.id, user.workspaceId, user.email, user.workspaceName);

      if (isNew) {
        analytics.track('trial_started', {
          method: 'direct',
          $set_once: { trial_started_at: new Date().toISOString() },
        })
      }

      return { user, token: idToken };
    } catch (error: any) {
      // Handle Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Email already in use');
      }
      if (error.code === 'auth/weak-password') {
        throw new Error('Password should be at least 6 characters');
      }
      if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Email/password sign-in is not enabled. Please enable it in Firebase Console.');
      }
      if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      }

      // Handle backend sync errors
      if (error instanceof AuthError) {
        if (error.statusCode === 403) {
          throw new Error('Account is deactivated. Please contact support.');
        } else if (error.statusCode === 500) {
          throw new Error('Server error. Please try again later.');
        }
      }

      throw error;
    }
  },
  
  async signOut(): Promise<void> {
    // Single Sign-Out: Redirect to centralized OptiPrime logout
    // This clears SSO session across all connected apps
    const AUTH_URL = import.meta.env.VITE_AUTH_URL;

    // Redirect to marketing site after logout:
    // - Production: optidev.ai
    // - Sandbox: sandbox-marketing.optiedge.cc
    // Falls back to current origin if VITE_MARKETING_URL not set
    const returnUrl = import.meta.env.VITE_MARKETING_URL || window.location.origin;

    if (!AUTH_URL) {
      console.error('VITE_AUTH_URL is not set. Cannot perform SSO logout.');
      throw new Error('Authentication URL not configured');
    }

    const logoutUrl = `${AUTH_URL}/logout?product=optidev&continue=${encodeURIComponent(returnUrl)}`;
    analytics.reset();
    useCurrencyStore.getState().reset();
    window.location.href = logoutUrl;
  },
  
  async getCurrentUser(): Promise<User | null> {
    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) return null;

    try {
      const { user } = await syncWithBackend(firebaseUser);
      this.currentUser = user;
      analytics.identify(user.id, user.workspaceId, user.email, user.workspaceName);
      return user;
    } catch (error) {
      // Only sign out on real auth issues (401, 403)
      if (error instanceof AuthError && (error.statusCode === 401 || error.statusCode === 403)) {
        isForceSigningOut = true;
        this.currentUser = null;
        await firebaseSignOut(firebaseAuth);
        return null;
      }

      // For other errors, keep existing session if available
      if (this.currentUser) {
        return this.currentUser;
      }

      console.error('Failed to get current user:', error);
      return null;
    }
  },
  
  onAuthStateChanged(callback: (user: User | null, error?: AuthError) => void): () => void {
    return firebaseOnAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      if (!firebaseUser) {
        // Skip callback if this is triggered by our force sign-out (already handled)
        if (isForceSigningOut) {
          isForceSigningOut = false;
          return;
        }
        this.currentUser = null;
        callback(null);
        return;
      }

      // Skip sync if already have user with same uid (token refresh case)
      // This prevents unnecessary backend calls on hourly token refresh
      if (this.currentUser && this.currentUser.id === firebaseUser.uid) {
        callback(this.currentUser);
        return;
      }

      // New user or first load - sync with backend
      try {
        const { user, isNew } = await syncWithBackend(firebaseUser);
        this.currentUser = user;
        analytics.identify(user.id, user.workspaceId, user.email, user.workspaceName);

        if (isNew) {
          analytics.track('trial_started', {
            method: 'oauth',
            $set_once: { trial_started_at: new Date().toISOString() },
          })
        } else {
          analytics.track('login', { method: 'oauth' })
        }

        callback(user);
      } catch (error) {
        console.error('Failed to sync user with backend:', error);

        // Only kick out user on real auth issues (401, 403)
        // For all other errors (network, 5xx, etc.), keep existing session
        if (error instanceof AuthError && (error.statusCode === 401 || error.statusCode === 403)) {
          isForceSigningOut = true;
          this.currentUser = null;
          callback(null, error);
          // Await signOut to ensure it completes before any redirect
          await firebaseSignOut(firebaseAuth);
          return;
        }

        // For non-401/403 errors: keep user logged in if we have existing session
        if (this.currentUser) {
          callback(this.currentUser);
        } else {
          // No existing session - this is initial login, retry on 5xx/network errors
          for (let attempt = 1; attempt <= 3; attempt++) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            try {
              const { user, isNew: retryIsNew } = await syncWithBackend(firebaseUser);
              this.currentUser = user;
              analytics.identify(user.id, user.workspaceId, user.email, user.workspaceName);

              if (retryIsNew) {
                analytics.track('trial_started', {
                  method: 'oauth',
                  $set_once: { trial_started_at: new Date().toISOString() },
                })
              } else {
                analytics.track('login', { method: 'oauth' })
              }

              callback(user);
              return;
            } catch (retryError) {
              if (retryError instanceof AuthError && (retryError.statusCode === 401 || retryError.statusCode === 403)) {
                isForceSigningOut = true;
                callback(null, retryError);
                await firebaseSignOut(firebaseAuth);
                return;
              }
            }
          }
          // All retries failed
          if (error instanceof AuthError) {
            callback(null, error);
          } else {
            callback(null);
          }
        }
      }
    });
  },
  
  async getIdToken(): Promise<string | null> {
    // In embed mode, request a fresh token from parent (scio3) via postMessage
    const { isEmbedded } = (await import('@/stores/embed-store')).useEmbedStore.getState();
    if (isEmbedded) {
      const { getActiveBridge } = await import('@/embed/OptiSignsBridge');
      const bridge = getActiveBridge();
      if (bridge) {
        try {
          // Same protocol as Designer: send ON_GET_TOKEN, parent responds with { sessionId }
          const response = await bridge.sendRequest<{ sessionId?: string }>('ON_GET_TOKEN');
          if (response?.sessionId) {
            const decoded = JSON.parse(atob(response.sessionId));
            return decoded.token;
          }
        } catch (err) {
          console.error('[Auth] Failed to get token from parent:', err);
        }
      }
      return null;
    }
    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) return null;
    return await firebaseUser.getIdToken();
  }
};

// Export Firebase instances
export { firebaseAuth, db };
export default { auth, firebaseAuth, db };